/*
================================================================================
File: src/pages/Dashboard.js
Description: The main dashboard screen displaying key metrics and todo lists.
================================================================================
                  <u>Server Admin Scripts:</u>
                  <li className="">updateProfile/Password/email change</li>
                  <li className="">sendMessage not sending</li>
                  <li className="">IFSSync = not tested</li>
                  <li className="">syncIFS = not tested</li>
*/
import React, { useMemo, useCallback, useEffect, useState } from "react";
import { useAdaptiveMMKV } from "../hooks/useAdaptiveMMKV";
import { storage } from "../App";
import instance from "../api/axios";
import PageContent from "../components/common/PageContent";

const StatCard = ({ title, value, icon, accent }) => (
  <div className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow-md border border-gray-200 dark:border-gray-700">
    <div className="flex items-center justify-between mb-3">
      <span className="text-sm font-medium text-gray-500 dark:text-gray-400">
        {title}
      </span>
      {icon && <div className={`p-2 rounded-lg ${accent}`}>{icon}</div>}
    </div>
    <p className="text-3xl font-bold text-gray-800 dark:text-gray-100">
      {value}
    </p>
  </div>
);

const PortfolioCard = ({ label, value }) => (
  <div className="bg-white dark:bg-gray-800 p-5 rounded-lg border border-gray-200 dark:border-gray-700">
    <p className="text-sm text-gray-500 dark:text-gray-400 mb-2">{label}</p>
    <p className="text-2xl font-bold text-orange-600 dark:text-orange-400">
      {value}
    </p>
  </div>
);

const ProfileCompletionCard = ({ user }) => {
  const profileGroups = [
    {
      label: "Company Profile",
      fields: ["co_name", "coemail", "mailing", "co_fax", "number"],
    },
    {
      label: "Investor Profile",
      fields: [
        "lend_cash",
        "websites",
        "phone",
        "inv_email",
        "money_from",
        "money_to",
        "prop_types",
        "investment_types",
        "counties",
        "fincance_type",
      ],
    },
    {
      label: "Contract Options",
      fields: [
        "preferr_title_name",
        "preferr_title_address",
        "default_period_option",
        "default_period_option_money",
        "earnest_money",
      ],
    },
  ];

  const incompleteItems = [];
  const incompleteByGroup = {};

  profileGroups.forEach(({ label, fields }) => {
    const incomplete = fields.filter((f) => user?.[f] === "");
    incompleteByGroup[label] = incomplete;
    incomplete.forEach((field) => {
      incompleteItems.push({ field, group: label });
    });
  });

  if (incompleteItems.length === 0) return null;

  const totalFields = 20;
  const completedFields = totalFields - incompleteItems.length;
  const percentage = Math.round((completedFields / totalFields) * 100);

  return (
    <div className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow-md border border-gray-200 dark:border-gray-700">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-semibold text-gray-700 dark:text-gray-200">
          Profile Setup
        </h3>
        <span className="text-sm font-medium text-orange-600 dark:text-orange-400">
          {percentage}% Complete
        </span>
      </div>

      <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2 mb-4">
        <div
          className="bg-orange-500 h-2 rounded-full transition-all"
          style={{ width: `${percentage}%` }}
        />
      </div>

      <p className="text-sm text-gray-600 dark:text-gray-300 mb-4">
        {incompleteItems.length} item{incompleteItems.length !== 1 ? "s" : ""}{" "}
        remaining
      </p>

      <div className="space-y-3 mb-5">
        {profileGroups.map(({ label }) => {
          const incomplete = incompleteByGroup[label];
          const isComplete = incomplete.length === 0;
          return (
            <div
              key={label}
              className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-700 rounded-lg"
            >
              <span className="text-sm text-gray-700 dark:text-gray-200">
                {label}
              </span>
              <div
                className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                  isComplete
                    ? "bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400"
                    : "bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-400"
                }`}
              >
                {isComplete ? (
                  <>
                    <svg
                      className="w-4 h-4 mr-1"
                      fill="currentColor"
                      viewBox="0 0 20 20"
                    >
                      <path
                        fillRule="evenodd"
                        d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                        clipRule="evenodd"
                      />
                    </svg>
                    Done
                  </>
                ) : (
                  `${incomplete.length} items`
                )}
              </div>
            </div>
          );
        })}
      </div>

      <ul className="space-y-2 mb-5">
        {incompleteItems.map(({ field, group }) => {
          const fieldLabels = {
            co_name: "Add company name",
            coemail: "Add company email",
            mailing: "Add company number",
            co_fax: "Add company fax",
            number: "Add company phone",
            lend_cash: "Add lending interest",
            websites: "Add website",
            phone: "Add investor phone",
            inv_email: "Add investor email",
            money_from: "Add min investment range",
            money_to: "Add max investment range",
            prop_types: "Add property types",
            investment_types: "Add investment attributes",
            counties: "Add investment counties",
            fincance_type: "Add finance types",
            preferr_title_name: "Add preferred title company",
            preferr_title_address: "Add title company address",
            default_period_option: "Add contract period option",
            default_period_option_money: "Add contract option money",
            earnest_money: "Add earnest money amount",
          };
          return (
            <li
              key={field}
              className="text-sm text-gray-600 dark:text-gray-300 flex items-center"
            >
              <span className="text-orange-500 mr-2">•</span>
              {fieldLabels[field] || field} ({group})
            </li>
          );
        })}
      </ul>

      <button className="w-full px-4 py-2 bg-orange-500 hover:bg-orange-600 text-white font-medium rounded-lg transition-colors">
        Go to Profile Settings →
      </button>
    </div>
  );
};

const Dashboard = ({ user, pulldata }) => {
  const [dummyLeads, setDummyLeads] = useAdaptiveMMKV(
    "user.Feed.Seller.Leads",
    storage,
  );
  const [error, setError] = useState(null);

  const pullleadsdata = useCallback(async () => {
    try {
      const response = await instance.get(
        `/LoadHomes.php?u=${user?.app_id}&type=leads`,
      );
      if (Array.isArray(response.data)) {
        setDummyLeads(response.data);
        setError(null);
      }
    } catch (e) {
      if (e.response) {
        setError(
          "Error fetching data: " +
            (e.response?.data?.message || e.message || "Unknown error")
        );
        console.error(
          "Error fetching data: ",
          e.response?.data?.message || e.message
        );
      } else if (e.request) {
        setError("Error: No response received from server.");
        console.error("Error: ", e.request);
      } else {
        setError("Error: " + e.message);
        console.error("Error: ", e.message);
      }
    }
  }, [user.app_id, setDummyLeads]);

  useEffect(() => {
    pullleadsdata();
    pulldata();
  }, [pulldata, pullleadsdata]);

  const stats = useMemo(() => {
    const leads = dummyLeads || [];
    const newLeads = leads.filter((lead) =>
      String(lead.status).includes("New Lead"),
    );

    // Helper function for safe numeric conversion
    const safeParseInt = (value) => {
      const parsed = parseInt(value || "0", 10);
      return isNaN(parsed) ? 0 : parsed;
    };

    const totalTaxValue = leads.reduce(
      (sum, lead) => sum + safeParseInt(lead?.property_assdTtlValue),
      0,
    );
    const totalCMAValue = leads.reduce(
      (sum, lead) => sum + safeParseInt(lead?.property_cma_value),
      0,
    );
    const totalAIValue = leads.reduce(
      (sum, lead) => sum + safeParseInt(lead?.zestimate),
      0,
    );
    const totalZestimateValue = leads.reduce(
      (sum, lead) => sum + safeParseInt(lead?.zestimate2),
      0,
    );

    return {
      totalLeads: leads.length.toLocaleString("en-US"),
      newLeads: newLeads.length.toLocaleString("en-US"),
      totalTaxValue: `$${totalTaxValue.toLocaleString("en-US")}`,
      totalCMAValue: `$${totalCMAValue.toLocaleString("en-US")}`,
      totalAIValue: `$${totalAIValue.toLocaleString("en-US")}`,
      totalZestimateValue: `$${totalZestimateValue.toLocaleString("en-US")}`,
    };
  }, [dummyLeads]);

  return (
    <PageContent title="Dashboard" error={error}>
      <div className="p-8 space-y-8">
        {/* Row 1: 4 stat cards with icons */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          <StatCard
            title="Total Leads"
            value={stats.totalLeads}
            icon={
              <svg
                className="w-6 h-6 text-green-600 dark:text-green-400"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01"
                />
              </svg>
            }
            accent="bg-green-100 dark:bg-green-900/30"
          />
          <StatCard
            title="New Leads"
            value={stats.newLeads}
            icon={
              <svg
                className="w-6 h-6 text-blue-600 dark:text-blue-400"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M16 12a4 4 0 10-8 0 4 4 0 008 0zm0 0v1.5a2.5 2.5 0 005 0V12a9 9 0 10-9 9m4.5-1.206a8.959 8.959 0 01-4.5 1.207"
                />
              </svg>
            }
            accent="bg-blue-100 dark:bg-blue-900/30"
          />
          <StatCard
            title="Skip Trace Credits"
            value={Math.max(
              0,
              parseInt(user?.skip_credits || "0", 10) || 0
            ).toLocaleString()}
            icon={
              <svg
                className="w-6 h-6 text-orange-600 dark:text-orange-400"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                />
              </svg>
            }
            accent="bg-orange-100 dark:bg-orange-900/30"
          />
          <StatCard
            title="AI-Query Credits"
            value={Math.max(0, parseInt(user?.credits || "0", 10) || 0).toLocaleString()}
            icon={
              <svg
                className="w-6 h-6 text-indigo-600 dark:text-indigo-400"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z"
                />
              </svg>
            }
            accent="bg-indigo-100 dark:bg-indigo-900/30"
          />
        </div>

        {/* Row 2: Portfolio Value Overview */}
        <div>
          <h2 className="text-lg font-semibold text-gray-700 dark:text-gray-200 mb-4">
            Portfolio Value Overview
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            <PortfolioCard label="Total CMA Value" value={stats.totalCMAValue} />
            <PortfolioCard label="Total AI Value" value={stats.totalAIValue} />
            <PortfolioCard label="Total Tax Value" value={stats.totalTaxValue} />
            <PortfolioCard
              label="Total Zestimate Value"
              value={stats.totalZestimateValue}
            />
          </div>
        </div>

        {/* Row 3: Profile Completion Card */}
        <ProfileCompletionCard user={user} />
      </div>
    </PageContent>
  );
};

export default Dashboard;
